import type { Lang } from "./language";

export const PATHWAY_PRICES = {
  basic: { MXN: 3500, USD: 200 },
  intermediate: { MXN: 4500, USD: 250 },
  advanced: { MXN: 6000, USD: 330 },
} as const;

export type PathwayKey = keyof typeof PATHWAY_PRICES;
export type CurrencyKey = "MXN" | "USD";

type PortalContent = {
  auth: {
    backHome: string;
    tagline: string;
    signInTitle: string;
    signUpTitle: string;
    signInLead: string;
    signUpLead: string;
    email: string;
    password: string;
    fullName: string;
    country: string;
    medicalArea: string;
    countryPlaceholder: string;
    medicalAreaPlaceholder: string;
    signIn: string;
    signUp: string;
    google: string;
    or: string;
    toSignUp: string;
    toSignIn: string;
    checkEmail: string;
    loading: string;
  };
  portal: {
    title: string;
    greeting: string;
    signOut: string;
    profileTitle: string;
    save: string;
    saved: string;
    applyTitle: string;
    applyLead: string;
    pathway: string;
    currency: string;
    notes: string;
    notesPlaceholder: string;
    submit: string;
    submitted: string;
    statusTitle: string;
    empty: string;
    fee: string;
    reviewNotes: string;
    payTitle: string;
    payLockedLead: string;
    payReadyLead: string;
    pay: string;
    paidLead: string;
    adminLink: string;
    groupTitle: string;
    groupNone: string;
    groupStarts: string;
    historyTitle: string;
    coupon: {
      label: string;
      placeholder: string;
      apply: string;
      remove: string;
      applied: string;
      errors: Record<string, string>;
    };

    statuses: Record<string, string>;
    pathways: Record<PathwayKey, string>;
  };
};

const es: PortalContent = {
  auth: {
    backHome: "Volver al sitio",
    tagline: "Portal del participante",
    signInTitle: "Inicia sesión",
    signUpTitle: "Crea tu cuenta",
    signInLead: "Accede para dar seguimiento a tu solicitud y a tu pago de participación.",
    signUpLead: "Regístrate para aplicar al programa de mentoría STM.",
    email: "Correo electrónico",
    password: "Contraseña",
    fullName: "Nombre completo",
    country: "País de origen",
    medicalArea: "Área de la medicina de interés",
    countryPlaceholder: "Ej. México",
    medicalAreaPlaceholder: "Ej. Cardiología, Pediatría, Cirugía general",
    signIn: "Entrar",
    signUp: "Crear cuenta",
    google: "Continuar con Google",
    or: "o",
    toSignUp: "¿No tienes cuenta? Regístrate",
    toSignIn: "¿Ya tienes cuenta? Inicia sesión",
    checkEmail: "Revisa tu correo para confirmar tu cuenta y luego inicia sesión.",
    loading: "Procesando…",
  },
  portal: {
    title: "Portal del participante",
    greeting: "Hola",
    signOut: "Cerrar sesión",
    profileTitle: "Tu perfil",
    save: "Guardar cambios",
    saved: "Perfil actualizado.",
    applyTitle: "Aplicar al programa",
    applyLead:
      "Elige la ruta de investigación que te interesa. La Dirección Académica STM revisará tu perfil antes de habilitar el pago.",
    pathway: "Ruta de investigación",
    currency: "Moneda de pago",
    notes: "Cuéntanos sobre ti",
    notesPlaceholder: "Experiencia previa en investigación, disponibilidad, expectativas…",
    submit: "Enviar solicitud",
    submitted: "Solicitud enviada. Te contactaremos tras la revisión.",
    statusTitle: "Tu solicitud",
    empty: "Aún no has enviado una solicitud.",
    fee: "Participación estimada",
    reviewNotes: "Notas de la Dirección Académica",
    payTitle: "Pago de participación",
    payLockedLead:
      "El pago se habilita cuando tu solicitud es aceptada por la Dirección Académica STM.",
    payReadyLead: "Tu solicitud fue aceptada. Formaliza tu lugar con el pago de participación.",
    pay: "Pagar participación",
    paidLead: "Pago recibido. Tu lugar está confirmado.",
    adminLink: "Dirección Académica",
    groupTitle: "Tu grupo",
    groupNone: "Aún no se te ha asignado un grupo.",
    groupStarts: "Inicio",
    historyTitle: "Historial de tu solicitud",
    coupon: {
      label: "Cupón de descuento",
      placeholder: "Ingresa tu código",
      apply: "Aplicar cupón",
      remove: "Quitar",
      applied: "Cupón {code} aplicado: {discount} de descuento.",
      errors: {
        not_found: "El código no existe.",
        inactive: "Este cupón está desactivado.",
        not_started: "Este cupón aún no está vigente.",
        expired: "Este cupón ya venció.",
        exhausted: "Este cupón alcanzó su número máximo de usos.",
        pathway: "Este cupón no aplica a tu ruta.",
        currency: "Este cupón no aplica a tu moneda de pago.",
      },
    },

    statuses: {
      submitted: "Enviada",
      under_review: "En revisión",
      accepted: "Aceptada",
      rejected: "No aceptada",
      paid: "Pagada",
    },
    pathways: {
      basic: "Ruta Básica — Reporte de caso",
      intermediate: "Ruta Intermedia — Narrative Review",
      advanced: "Ruta Avanzada — Revisión sistemática / metaanálisis",
    },
  },
};

const en: PortalContent = {
  auth: {
    backHome: "Back to site",
    tagline: "Participant portal",
    signInTitle: "Sign in",
    signUpTitle: "Create your account",
    signInLead: "Sign in to track your application and your participation payment.",
    signUpLead: "Register to apply to the STM mentorship program.",
    email: "Email",
    password: "Password",
    fullName: "Full name",
    country: "Country of origin",
    medicalArea: "Medical area of interest",
    countryPlaceholder: "e.g. Mexico",
    medicalAreaPlaceholder: "e.g. Cardiology, Pediatrics, General surgery",
    signIn: "Sign in",
    signUp: "Create account",
    google: "Continue with Google",
    or: "or",
    toSignUp: "No account yet? Register",
    toSignIn: "Already have an account? Sign in",
    checkEmail: "Check your email to confirm your account, then sign in.",
    loading: "Working…",
  },
  portal: {
    title: "Participant portal",
    greeting: "Hello",
    signOut: "Sign out",
    profileTitle: "Your profile",
    save: "Save changes",
    saved: "Profile updated.",
    applyTitle: "Apply to the program",
    applyLead:
      "Choose the research pathway you are interested in. STM Academic Direction reviews your profile before payment is enabled.",
    pathway: "Research pathway",
    currency: "Payment currency",
    notes: "Tell us about you",
    notesPlaceholder: "Previous research experience, availability, expectations…",
    submit: "Submit application",
    submitted: "Application submitted. We will contact you after the review.",
    statusTitle: "Your application",
    empty: "You have not submitted an application yet.",
    fee: "Estimated participation fee",
    reviewNotes: "Academic Direction notes",
    payTitle: "Participation payment",
    payLockedLead: "Payment is enabled once STM Academic Direction accepts your application.",
    payReadyLead: "Your application was accepted. Secure your seat with the participation payment.",
    pay: "Pay participation fee",
    paidLead: "Payment received. Your seat is confirmed.",
    adminLink: "Academic Direction",
    groupTitle: "Your group",
    groupNone: "You have not been assigned to a group yet.",
    groupStarts: "Starts",
    historyTitle: "Your application history",
    coupon: {
      label: "Discount coupon",
      placeholder: "Enter your code",
      apply: "Apply coupon",
      remove: "Remove",
      applied: "Coupon {code} applied: {discount} off.",
      errors: {
        not_found: "This code does not exist.",
        inactive: "This coupon is deactivated.",
        not_started: "This coupon is not valid yet.",
        expired: "This coupon has expired.",
        exhausted: "This coupon reached its maximum number of uses.",
        pathway: "This coupon does not apply to your pathway.",
        currency: "This coupon does not apply to your payment currency.",
      },
    },

    statuses: {
      submitted: "Submitted",
      under_review: "Under review",
      accepted: "Accepted",
      rejected: "Not accepted",
      paid: "Paid",
    },
    pathways: {
      basic: "Basic pathway — Case report",
      intermediate: "Intermediate pathway — Narrative review",
      advanced: "Advanced pathway — Systematic review / meta-analysis",
    },
  },
};

export const portalContent: Record<Lang, PortalContent> = { es, en };
